import React, { useMemo, useState } from 'react';
import { LuLink, LuX } from 'react-icons/lu';

import { getProviderCapabilities } from '../../entities/provider';
import { useProjects } from '../../shared/contexts/Projects.provider';

export interface AttachedProject {
  id: string;
  name: string;
  provider?: string;
}

interface AttachProjectControlProps {
  attachedName?: string;
  onAttach: (project: AttachedProject) => void;
}

// Studio (general chat) sessions start unbound. This lets a user pin an ongoing conversation to a
// real project at any point — and switch it again later — without starting a new chat. It only
// ever sets local component state (see SessionChatView), which the existing per-message
// context.boardId/connectedAgents construction already picks up on the next send; there's nothing
// to persist server-side, so reopening the session later starts unattached again.
export const AttachProjectControl: React.FC<AttachProjectControlProps> = ({ attachedName, onAttach }) => {
  const projects = useProjects();
  const [isOpen, setIsOpen] = useState(false);
  const [value, setValue] = useState('');

  const projectsByProvider = useMemo(() => {
    const groups: Record<string, typeof projects> = {};
    for (const p of projects) {
      const provider = p.provider ?? 'jira';
      (groups[provider] ??= []).push(p);
    }
    return groups;
  }, [projects]);

  if (!isOpen) {
    return (
      <button className="btn btn-ghost btn-xs gap-1.5" onClick={() => setIsOpen(true)}>
        <LuLink size={12} className={attachedName ? 'text-primary' : 'opacity-60'} />
        {attachedName ? (
          <>
            Attached: <b>{attachedName}</b> <span className="opacity-50">· Change</span>
          </>
        ) : (
          'Attach project'
        )}
      </button>
    );
  }

  const handleAttach = () => {
    const picked = projects.find((p) => (p._id ?? p.id) === value);
    if (!picked) return;
    const id = picked._id ?? picked.id ?? value;
    onAttach({ id, name: picked.name ?? id, provider: picked.provider });
    setIsOpen(false);
    setValue('');
  };

  return (
    <div className="inline-flex items-center gap-2 rounded-lg bg-base-200 p-1 pl-2">
      <span className="text-[10px] uppercase tracking-wide text-base-content/40 shrink-0">Attach</span>
      <select
        className="select select-bordered select-xs bg-base-100"
        value={value}
        onChange={(e) => setValue(e.target.value)}
      >
        <option value="">Choose a project…</option>
        {Object.entries(projectsByProvider).map(([provider, group]) => (
          <optgroup key={provider} label={getProviderCapabilities(provider).label}>
            {group.map((p) => {
              const id = p._id ?? p.id ?? '';
              return (
                <option key={id} value={id}>
                  {p.name ?? id}
                </option>
              );
            })}
          </optgroup>
        ))}
      </select>
      <button className="btn btn-primary btn-xs" disabled={!value} onClick={handleAttach}>
        Attach
      </button>
      <button className="btn btn-ghost btn-xs btn-circle" onClick={() => setIsOpen(false)} title="Cancel">
        <LuX size={12} />
      </button>
    </div>
  );
};

export default AttachProjectControl;
